/**
 * La finestra per condividere la mappa con chi non ha un accesso (27/10/2026): si scrive per chi è il link, si sceglie
 * quanto vale (7, 30 o 90 giorni), lo si crea e lo si copia. Sotto, i link creati: quando scadono, quante volte sono
 * stati aperti, e il bottone per ritirarli (con una domanda prima, nella finestra della pagina: mai quelle del browser).
 */
import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar,
  TextField, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Close, ContentCopy, LinkOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  DESTINATARIO_MASSIMO,
  DURATA_CONDIVISIONE_PREDEFINITA,
  DURATE_CONDIVISIONE_GIORNI,
  type CondivisioneMappa,
  type DurataCondivisione,
} from '@shared/admin/mappaAziende';
import { tokens } from '../../theme';
import { creaLinkMappa, motivoErrore, ritiraLinkMappa } from '../../services/mappaAziendeService';
import type { FormatiMappa } from './formati';

interface Props {
  aperta: boolean;
  onChiudi: () => void;
  condivisioni: readonly CondivisioneMappa[];
  onCondivisioni: (condivisioni: CondivisioneMappa[]) => void;
  formati: FormatiMappa;
}

export const CondividiMappa: React.FC<Props> = ({ aperta, onChiudi, condivisioni, onCondivisioni, formati }) => {
  const { t } = useTranslation('mappa');
  const theme = useTheme();
  const telefono = useMediaQuery(theme.breakpoints.down('sm'));
  const [destinatario, setDestinatario] = useState('');
  const [giorni, setGiorni] = useState<DurataCondivisione>(DURATA_CONDIVISIONE_PREDEFINITA);
  const [creando, setCreando] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [creata, setCreata] = useState<CondivisioneMappa | null>(null);
  const [daRitirare, setDaRitirare] = useState<CondivisioneMappa | null>(null);
  const [ritirando, setRitirando] = useState(false);
  const [avviso, setAvviso] = useState<string | null>(null);
  const campoLink = useRef<HTMLInputElement>(null);

  const crea = async () => {
    setCreando(true);
    setErrore(null);
    try {
      const { condivisione } = await creaLinkMappa(destinatario, giorni);
      setCreata(condivisione);
      onCondivisioni([condivisione, ...condivisioni]);
    } catch (e) {
      setErrore(t('condividi.errore', { motivo: motivoErrore(e) }));
    } finally {
      setCreando(false);
    }
  };

  const copia = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setAvviso(t('condividi.copiato'));
    } catch {
      campoLink.current?.select();
      setAvviso(t('condividi.copia_non_riuscita'));
    }
  };

  const ritira = async () => {
    if (!daRitirare) return;
    setRitirando(true);
    try {
      const { condivisione } = await ritiraLinkMappa(daRitirare.codice);
      onCondivisioni(condivisioni.filter((c) => c.codice !== condivisione.codice));
      if (creata?.codice === condivisione.codice) setCreata(null);
      setAvviso(t('condividi.ritirato', { destinatario: condivisione.destinatario }));
      setDaRitirare(null);
    } catch (e) {
      setErrore(t('condividi.errore', { motivo: motivoErrore(e) }));
      setDaRitirare(null);
    } finally {
      setRitirando(false);
    }
  };

  const ricomincia = () => {
    setCreata(null);
    setDestinatario('');
    setGiorni(DURATA_CONDIVISIONE_PREDEFINITA);
  };

  return (
    <>
      <Dialog
        open={aperta}
        onClose={onChiudi}
        fullScreen={telefono}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: telefono ? 0 : 2, border: telefono ? 0 : `1px solid ${tokens.color.line}` } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1, fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.4rem', letterSpacing: '-0.015em' }}>
          <Box component="span" sx={{ flex: 1 }}>{t('condividi.titolo')}</Box>
          <IconButton onClick={onChiudi} aria-label={t('condividi.chiudi')} sx={{ width: 44, height: 44 }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.88rem', color: tokens.color.ash, lineHeight: 1.55, maxWidth: '62ch', mb: 2.5 }}>
            {t('condividi.spiegazione')}
          </Typography>

          {errore && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrore(null)}>{errore}</Alert>}

          {creata ? (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: tokens.color.bone, border: `1px solid ${tokens.color.line}`, mb: 3 }}>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, mb: 1.5 }}>
                {t('condividi.pronto', { destinatario: creata.destinatario, data: formati.giorno(creata.scade_il) })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  inputRef={campoLink}
                  value={creata.link}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, sx: { fontSize: '0.82rem', bgcolor: tokens.color.paper } }}
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<ContentCopy fontSize="small" />}
                  onClick={() => void copia(creata.link)}
                  sx={{ minHeight: 40, flexShrink: 0, bgcolor: tokens.color.graphite, '&:hover': { bgcolor: '#000' }, textTransform: 'none', fontWeight: 600 }}
                >
                  {t('condividi.copia')}
                </Button>
              </Box>
              <Button onClick={ricomincia} sx={{ mt: 1.5, textTransform: 'none', color: tokens.color.graphite, px: 0 }}>{t('condividi.altro')}</Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={(e) => { e.preventDefault(); void crea(); }} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3 }}>
              <TextField
                label={t('condividi.destinatario')}
                placeholder={t('condividi.destinatario_esempio')}
                helperText={t('condividi.destinatario_aiuto')}
                value={destinatario}
                onChange={(e) => setDestinatario(e.target.value)}
                inputProps={{ maxLength: DESTINATARIO_MASSIMO }}
                required
                fullWidth
                autoFocus
              />
              <Box>
                <Typography id="durata-link-mappa" sx={{ fontSize: '0.8rem', color: tokens.color.ash, mb: 0.75 }}>{t('condividi.durata')}</Typography>
                <ToggleButtonGroup
                  exclusive
                  value={giorni}
                  onChange={(_, v: DurataCondivisione | null) => v && setGiorni(v)}
                  aria-labelledby="durata-link-mappa"
                  sx={{ '& .MuiToggleButton-root': { minHeight: 44, px: 2.5, textTransform: 'none', fontWeight: 600 } }}
                >
                  {DURATE_CONDIVISIONE_GIORNI.map((g) => (
                    <ToggleButton key={g} value={g}>{t('condividi.giorni', { count: g })}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={creando || !destinatario.trim()}
                  startIcon={creando ? <CircularProgress size={16} color="inherit" /> : undefined}
                  sx={{ minHeight: 44, px: 3, bgcolor: tokens.color.graphite, '&:hover': { bgcolor: '#000' }, textTransform: 'none', fontWeight: 600 }}
                >
                  {creando ? t('condividi.creando') : t('condividi.crea')}
                </Button>
              </Box>
            </Box>
          )}

          <Typography component="h3" sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 1 }}>{t('condividi.attivi')}</Typography>
          {condivisioni.length === 0 ? (
            <Typography sx={{ fontSize: '0.85rem', color: tokens.color.ash }}>{t('condividi.nessun_link')}</Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {condivisioni.map((c) => {
                const momento = formati.momento(c.ultima_apertura_il);
                const attiva = c.stato === 'attiva';
                return (
                  <Box component="li" key={c.codice} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25, borderTop: `1px solid ${tokens.color.lineSoft}`, opacity: attiva ? 1 : 0.55 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{c.destinatario}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: tokens.color.ash }}>
                        {[
                          attiva ? t('condividi.scade_il', { data: formati.giorno(c.scade_il) }) : t('condividi.scaduto_il', { data: formati.giorno(c.scade_il) }),
                          c.aperture > 0 && momento
                            ? t('condividi.aperto', { count: c.aperture, quando: momento.giorno === 'oggi' || momento.giorno === 'ieri' ? t(`scheda.ultimo_uso_${momento.giorno}`, { ora: momento.ora }) : momento.data })
                            : t('condividi.mai_aperto'),
                        ].join(' · ')}
                      </Typography>
                    </Box>
                    {attiva && (
                      <IconButton aria-label={t('condividi.copia')} onClick={() => void copia(c.link)} sx={{ width: 44, height: 44 }}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    )}
                    <Button
                      onClick={() => setDaRitirare(c)}
                      startIcon={<LinkOff fontSize="small" />}
                      sx={{ minHeight: 40, textTransform: 'none', color: tokens.color.danger, flexShrink: 0 }}
                    >
                      {t('condividi.ritira')}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!daRitirare} onClose={() => !ritirando && setDaRitirare(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.2rem' }}>
          {t('condividi.ritira_titolo', { destinatario: daRitirare?.destinatario ?? '' })}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.88rem', color: tokens.color.ash, lineHeight: 1.55 }}>{t('condividi.ritira_testo')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDaRitirare(null)} disabled={ritirando} sx={{ textTransform: 'none', minHeight: 40 }}>{t('condividi.annulla')}</Button>
          <Button
            onClick={() => void ritira()}
            disabled={ritirando}
            variant="contained"
            color="error"
            disableElevation
            startIcon={ritirando ? <CircularProgress size={16} color="inherit" /> : <LinkOff fontSize="small" />}
            sx={{ textTransform: 'none', minHeight: 40, fontWeight: 600 }}
          >
            {t('condividi.ritira_conferma')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!avviso} autoHideDuration={4000} onClose={() => setAvviso(null)} message={avviso} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
};
